/** @jsxImportSource @emotion/react */
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { css } from '@emotion/react';
import { renderAsync } from 'docx-preview';
import { Column } from '../layout/flexbox';
import * as Buttons from '../forms/elements/buttons';
import { IAppState } from '../core/redux/IAppState';
import { loadTrainingDocumentsAction } from './loadTrainingDocumentsAction';
import { LargeText, PAGE_COLUMN, PAGE_CONTENT, PAGE_TITLE } from '../styles/styles';
import { Loader } from '../forms/elements/loader/Loader';
import ErrorView from '../forms/ErrorView';
import { LIGHT_GREY } from '../styles/colors';
import { getResource } from '../language/resources/getResource';
import { apiGetTrainingDocumentContent } from '../core/redux/api/endpoints';
import { HELP_AND_TRAINING_ROUTE } from '../core/routes';


const DOCX_OPTIONS = {
  breakPages:    true,
  inWrapper:     true,
  renderHeaders: true,
  renderFooters: true,
};

const backButton = css`
  align-self: flex-start;
  margin-bottom: 10px;
`;

const loaderAlignment = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 25px 0;
`;

const docLoader = css`
  width: 100px;
  height: 100px;
  margin-bottom: 20px;
`;

const loadingMsg = css`
  display: block;
  ${LargeText};
`;

const viewerScroller = css`
  overflow: auto;
  background-color: ${LIGHT_GREY};
`;


/**
 * TrainingDocumentViewerPage: fetches a training document's bytes and renders it read-only in the app with
 * docx-preview. Reached from HelpAndTrainingPage (or a deep link) at HELP_AND_TRAINING_ROUTE/:documentId.
 * Deliberately offers no download control.
 */
export default function TrainingDocumentViewerPage() {
  const { documentId } = useParams<{ documentId: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const documents = useSelector((state: IAppState) => state.trainingDocuments);
  const bearerToken = useSelector((state: IAppState) => state.auth.bearerToken);
  const resources = useSelector((state: IAppState) => state.locale);

  const containerRef = useRef<HTMLDivElement>(null);
  const [isRendering, setIsRendering] = useState(true);
  const [hasFailed, setHasFailed] = useState(false);

  const title = documents?.find(doc => String(doc.id) === documentId)?.title;

  // The list page clears the slice on unmount, so (re)load it here for the title — also covers deep links.
  useEffect(() => {
    if (!documents) {
      dispatch(loadTrainingDocumentsAction());
    }
  }, [documents, dispatch]);

  useEffect(() => {
    let cancelled = false;

    const fetchAndRender = async () => {
      setIsRendering(true);
      setHasFailed(false);
      try {
        const response = await fetch(apiGetTrainingDocumentContent(documentId!), {
          method:  'GET',
          headers: { Authorization: `Bearer ${bearerToken}` },
        });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const blob = await response.blob();
        if (cancelled || !containerRef.current) {
          return;
        }
        containerRef.current.innerHTML = '';
        await renderAsync(blob, containerRef.current, undefined, DOCX_OPTIONS);
      } catch {
        if (!cancelled) {
          setHasFailed(true);
        }
      } finally {
        if (!cancelled) {
          setIsRendering(false);
        }
      }
    };

    fetchAndRender();
    return () => { cancelled = true; };
  }, [documentId, bearerToken]);

  return (
    <Column rootCss={PAGE_COLUMN}>
      <span css={PAGE_TITLE}>{title ?? getResource(resources, 'HELP_TRAINING_PAGE_TITLE')}</span>
      <div css={PAGE_CONTENT}>
        <Buttons.BigNeutral type="button" css={backButton} onClick={() => navigate(HELP_AND_TRAINING_ROUTE)}>
          {getResource(resources, 'HELP_TRAINING_BACK')}
        </Buttons.BigNeutral>
        { // Loading
          isRendering
          && <div css={loaderAlignment}>
            <Loader rootCss={docLoader}/>
            <span css={loadingMsg}>{getResource(resources, 'HELP_TRAINING_LOADING')}</span>
          </div>
        }
        {/* Error */}
        <ErrorView error={hasFailed ? getResource(resources, 'HELP_TRAINING_VIEWER_FAILED') : undefined}/>
        {/* Document */}
        <div css={viewerScroller}>
          <div ref={containerRef}/>
        </div>
      </div>
    </Column>
  );
}
